import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import ImageUpload from '@/components/ImageUpload';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, ArrowUp, ArrowDown } from 'lucide-react';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title_tr: '',
    title_en: '',
    description_tr: '',
    description_en: '',
    client: '',
    category: '',
    image_url: '',
    year: new Date().getFullYear(),
    display_order: 0,
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, formData);
        toast.success('Proje güncellendi!');
      } else {
        await axios.post(`${API}/projects`, formData);
        toast.success('Proje eklendi!');
      }
      fetchProjects();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Bir hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu projeyi silmek istediğinizden emin misiniz?')) return;

    try {
      await axios.delete(`${API}/projects/${id}`);
      toast.success('Proje silindi!');
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Bir hata oluştu!');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title_tr: project.title_tr,
      title_en: project.title_en,
      description_tr: project.description_tr,
      description_en: project.description_en,
      client: project.client,
      category: project.category,
      image_url: project.image_url,
      year: project.year,
      display_order: project.display_order || 0,
    });
    setDialogOpen(true);
  };

  const handleReorder = async (project, direction) => {
    try {
      const currentOrder = project.display_order || 0;
      const newOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;
      
      if (newOrder < 1) {
        toast.error('Daha yukarı taşınamaz!');
        return;
      }

      // Update project with new order
      const updatedData = {
        ...project,
        display_order: newOrder,
      };
      delete updatedData.id;
      delete updatedData.created_at;

      await axios.put(`${API}/projects/${project.id}`, updatedData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });

      toast.success('Sıralama güncellendi!');
      fetchProjects(); // Refresh list
    } catch (error) {
      console.error('Error reordering project:', error);
      toast.error('Sıralama güncellenemedi!');
    }
  };

  const resetForm = () => {
    setEditingProject(null);
    setFormData({
      title_tr: '',
      title_en: '',
      description_tr: '',
      description_en: '',
      client: '',
      category: '',
      image_url: '',
      year: new Date().getFullYear(),
      display_order: 0,
    });
  };

  const handleChange = (e) => {
    const value = e.target.type === 'number' ? parseInt(e.target.value) : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 
            className="text-3xl font-bold text-gray-900"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="projects-title"
          >
            Projeler Yönetimi
          </h1>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-project-btn">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Proje
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProject ? 'Proje Düzenle' : 'Yeni Proje Ekle'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="title_tr">Başlık (TR)</Label>
                    <Input
                      id="title_tr"
                      name="title_tr"
                      value={formData.title_tr}
                      onChange={handleChange}
                      required
                      data-testid="project-title-tr"
                    />
                  </div>
                  <div>
                    <Label htmlFor="title_en">Title (EN)</Label>
                    <Input
                      id="title_en"
                      name="title_en"
                      value={formData.title_en}
                      onChange={handleChange}
                      required
                      data-testid="project-title-en"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="description_tr">Açıklama (TR)</Label>
                  <Textarea
                    id="description_tr"
                    name="description_tr"
                    value={formData.description_tr}
                    onChange={handleChange}
                    required
                    rows={3}
                    data-testid="project-desc-tr"
                  />
                </div>
                <div>
                  <Label htmlFor="description_en">Description (EN)</Label>
                  <Textarea
                    id="description_en"
                    name="description_en"
                    value={formData.description_en}
                    onChange={handleChange}
                    required
                    rows={3}
                    data-testid="project-desc-en"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="client">Müşteri</Label>
                    <Input
                      id="client"
                      name="client"
                      value={formData.client}
                      onChange={handleChange}
                      required
                      data-testid="project-client"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Kategori</Label>
                    <Input
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      data-testid="project-category"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="year">Yıl</Label>
                    <Input
                      id="year"
                      name="year"
                      type="number"
                      value={formData.year}
                      onChange={handleChange}
                      required
                      data-testid="project-year"
                    />
                  </div>
                  <div>
                    <Label htmlFor="display_order">Sıra Numarası</Label>
                    <Input
                      id="display_order"
                      name="display_order"
                      type="number"
                      value={formData.display_order}
                      onChange={handleChange}
                      placeholder="0 = En sona ekle"
                      data-testid="project-order"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Numara girerseniz, o ve sonrası +1 kaydırılır
                    </p>
                  </div>
                </div>
                <ImageUpload
                  label="Proje Görseli"
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                />
                <Button type="submit" disabled={loading} className="w-full" data-testid="project-submit">
                  {loading ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div key={project.id} className="bg-white rounded-xl overflow-hidden border border-gray-200" data-testid={`project-card-${project.id}`}>
              <img src={project.image_url} alt={project.title_tr} className="w-full h-48 object-cover" />
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded">#{project.display_order || 0}</span>
                  <h3 className="text-xl font-bold text-gray-900">{project.title_tr}</h3>
                </div>
                <p className="text-gray-600 mb-2">{project.client}</p>
                <p className="text-sm text-gray-500 mb-4">{project.year}</p>
                <div className="flex gap-2 flex-wrap">
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleReorder(project, 'up')}
                      title="Yukarı taşı"
                      data-testid={`move-up-${project.id}`}
                    >
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleReorder(project, 'down')}
                      title="Aşağı taşı"
                      data-testid={`move-down-${project.id}`}
                    >
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleEdit(project)} data-testid={`edit-project-${project.id}`}>
                    <Edit className="w-4 h-4 mr-1" />
                    Düzenle
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(project.id)} data-testid={`delete-project-${project.id}`}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Sil
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProjects;